(() => {
  let shippingTotal;
  let shippingDisplay;
  const shippingMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  class CrafiiShippingProgress extends HTMLElement {
    connectedCallback() {
      this.startFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.renderProgress();
      });
    }
    renderProgress() {
      const fill = this.querySelector('.crafii-shipping-fill');
      const marker = this.querySelector('.crafii-shipping-marker');
      if (!fill || !marker) return;
      const total = Math.max(0, Number(this.dataset.total) || 0);
      const threshold = Number(this.dataset.threshold) || 4990;
      this.target = Math.min(total / threshold, 1) * 100;
      this.from = shippingDisplay ?? this.target;
      const increased = shippingTotal !== undefined && total > shippingTotal && shippingTotal < threshold;
      shippingTotal = total;
      shippingDisplay = this.target;
      this.dataset.shippingReady = 'true';
      this.dataset.shippingFrom = String(this.from);
      fill.style.width = `${this.target}%`;
      marker.style.left = `${this.target}%`;
      if (this.hidden || shippingMotion.matches || typeof fill.animate !== 'function') return;
      const pop = () => {
        if (!increased || !this.isConnected || shippingMotion.matches) return;
        this.pulse = this.querySelector('.crafii-shipping-marker__icon').animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(1.22)', offset: 0.45 }, { transform: 'scale(1)' }],
          { duration: 480, easing: 'ease-out' }
        );
      };
      if (Math.abs(this.from - this.target) < 0.01) return;
      const timing = { duration: 900, easing: 'cubic-bezier(.22,1,.36,1)' };
      this.travel = fill.animate([{ width: `${this.from}%` }, { width: `${this.target}%` }], timing);
      this.markerTravel = marker.animate([{ left: `${this.from}%` }, { left: `${this.target}%` }], timing);
      this.travel.finished.then(pop).catch(() => {});
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.startFrame);
      const progress = this.travel?.effect.getComputedTiming().progress;
      if (typeof progress === 'number') shippingDisplay = this.from + (this.target - this.from) * progress;
      this.travel?.cancel();
      this.markerTravel?.cancel();
      this.pulse?.cancel();
    }
    finishMotion() {
      this.travel?.finish();
      this.markerTravel?.finish();
      this.pulse?.cancel();
      shippingDisplay = this.target;
    }
  }
  if (!customElements.get('crafii-shipping-progress')) customElements.define('crafii-shipping-progress', CrafiiShippingProgress);
  shippingMotion.addEventListener('change', () => {
    if (shippingMotion.matches) document.querySelectorAll('crafii-shipping-progress').forEach(progress => progress.finishMotion());
  });
  const paymentInkRoots = new WeakSet();
  const applyPaymentInk = terms => {
    const root = terms.shadowRoot;
    if (!root || paymentInkRoots.has(root)) return;
    paymentInkRoots.add(root);
    const paint = () => {
      if (root.querySelector('style[data-crafii-payment-ink]')) return;
      const style = document.createElement('style');
      style.dataset.crafiiPaymentInk = '';
      style.textContent = '.text-purple-primary,.\\!text-purple-primary{color:#000!important}.fill-purple-primary{fill:#000!important}.bg-purple-primary{background-color:#000!important}';
      root.append(style);
    };
    paint();
    new MutationObserver(paint).observe(root, { childList: true, subtree: true });
  };
  customElements.whenDefined('shopify-payment-terms').then(() => {
    document.querySelectorAll('shopify-payment-terms').forEach(applyPaymentInk);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.matches('shopify-payment-terms')) applyPaymentInk(node);
      node.querySelectorAll('shopify-payment-terms').forEach(applyPaymentInk);
    }))).observe(document.body, { childList: true, subtree: true });
  });
  const menus = [...document.querySelectorAll('.crafii-studio-menu')];
  const closeMenu = (menu, restoreFocus = false) => {
    if (!menu.open || menu.dataset.closing) return;
    menu.dataset.closing = 'true';
    const finish = () => {
      menu.open = false;
      delete menu.dataset.closing;
      if (restoreFocus) menu.querySelector('summary').focus();
    };
    const motion = window.CrafiiMotion?.exit(menu.querySelector('.crafii-studio-menu__panel'), { y: -6 });
    motion ? motion.finally(finish) : finish();
  };
  document.addEventListener('click', event => menus.forEach(menu => {
    if (menu.open && !menu.contains(event.target)) closeMenu(menu);
  }));
  menus.forEach(menu => {
    menu.querySelector('summary').addEventListener('click', event => {
      if (menu.open) { event.preventDefault(); closeMenu(menu); }
    });
    menu.addEventListener('toggle', () => {
      if (menu.open) window.CrafiiMotion?.enter(menu.querySelector('.crafii-studio-menu__panel'), { duration: 260, y: -8 });
    });
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); closeMenu(menu, true); }
    });
  });
  const dialog = document.getElementById('CrafiiWelcome');
  if (dialog) {
    let previousFocus;
    let dismissed = false;
    const joined = 'crafii-welcome-joined';
    const visitKey = 'crafii-welcome-visit-v2';
    let shownThisVisit = false;
    let timer;
    try { shownThisVisit = sessionStorage.getItem(visitKey) === '1'; } catch {}
    const navigationType = window.performance?.getEntriesByType?.('navigation')[0]?.type || 'navigate';
    let sameSiteReferrer = false;
    try { sameSiteReferrer = new URL(document.referrer).origin === window.location.origin; } catch {}
    // Address-bar and external arrivals start a visit; reloads and store links retain it.
    if (navigationType === 'navigate' && !sameSiteReferrer) {
      shownThisVisit = false;
      try { sessionStorage.setItem(visitKey, '0'); } catch {}
    }
    const permitted = () => {
      if (dismissed || shownThisVisit) return false;
      try { if (sessionStorage.getItem(visitKey) === '1') return false; } catch {}
      try {
        return !localStorage.getItem(joined);
      } catch { return true; }
    };
    const openers = [...document.querySelectorAll('[data-welcome-open]')];
    let closing = false;
    const open = event => {
      if (dialog.open || closing || document.querySelector('cart-drawer.active,product-modal[open],.cf-policy-dialog[open]')) return false;
      previousFocus = event?.currentTarget || (document.activeElement === document.body ? openers[0] : document.activeElement);
      dialog.showModal();
      shownThisVisit = true;
      clearTimeout(timer);
      try { sessionStorage.setItem(visitKey, '1'); } catch {}
      document.body.classList.add('cf-welcome-open');
      openers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      dialog.querySelector('input[type=email]')?.focus({ preventScroll: true });
      window.CrafiiMotion?.enter(dialog, { duration: 560, y: 16 });
      return true;
    };
    const close = (immediate = false, restoreFocus = true) => {
      if (!dialog.open || closing) return;
      closing = true;
      dialog.classList.add('cf-welcome--closing');
      dismissed = true;
      const finish = () => {
        dialog.close();
        dialog.classList.remove('cf-welcome--closing');
        document.body.classList.remove('cf-welcome-open');
        openers.forEach(button => button.setAttribute('aria-expanded', 'false'));
        closing = false;
        if (restoreFocus && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
      };
      const motion = !immediate && window.CrafiiMotion?.exit(dialog, { y: 8 });
      motion ? motion.finally(finish) : finish();
    };
    dialog.querySelectorAll('[data-welcome-close]').forEach(button => button.addEventListener('click', () => close()));
    openers.forEach(button => button.addEventListener('click', open));
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
    });
    document.addEventListener('crafii:cart-opening', () => close(true, false));
    const maybeOpen = () => {
      if (dismissed || !permitted() || document.querySelector('main[data-template="cart"]')) return;
      const occupied = document.hidden || document.querySelector('dialog[open], cart-drawer.active, product-modal[open], .crafii-header-search details[open], .crafii-studio-menu[open]') || document.activeElement?.matches('input,textarea,select,[contenteditable="true"]');
      if (occupied) {
        timer = setTimeout(maybeOpen, 1000);
        return;
      }
      open();
    };
    timer = setTimeout(maybeOpen, 3500);
    window.addEventListener('pagehide', () => clearTimeout(timer));
    window.addEventListener('pageshow', event => {
      if (event.persisted && permitted()) timer = setTimeout(maybeOpen, 3500);
    });
    if (dialog.querySelector('[data-welcome-success]')) {
      try { localStorage.setItem(joined, '1'); } catch {}
      open();
    } else if (dialog.querySelector('[data-welcome-error]')) open();
  }
  document.addEventListener('submit', async event => {
    const form = event.target.closest('.crafii-add-form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type=submit]');
    if (!button || button.disabled) return;
    const status = form.querySelector('.crafii-add-status');
    const label = button.textContent;
    button.disabled = true;
    if (status) status.textContent = window.crafiiStudioStrings.adding;
    try {
      const data = new FormData(form);
      data.append('sections', 'cart-drawer,cart-icon-bubble');
      data.append('sections_url', location.pathname);
      const response = await fetch(form.action, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || window.crafiiStudioStrings.error);
      const drawer = document.querySelector('cart-drawer');
      if (drawer && result.sections && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty'); drawer.setActiveElement?.(button); drawer.renderContents(result);
      } else location.assign(window.routes.cart_url);
      if (status) status.textContent = window.crafiiStudioStrings.added;
    } catch (error) { if (status) status.textContent = error.message || window.crafiiStudioStrings.error; }
    finally { button.disabled = false; button.textContent = label; }
  });
})();
